/* eslint-disable react/no-unstable-nested-components */
import { lazy, useCallback, useRef } from "react"
import type { FC, LazyExoticComponent, ReactElement, RefObject } from "react"

import { QueryClient, useQueryClient } from "@tanstack/react-query"
import map from "lodash-es/map"
import values from "lodash-es/values"
import { ErrorBoundary } from "react-error-boundary"
import { type NavigateFunction, useNavigate } from "react-router-dom"

import {
    ESpinnerSize,
    ETypographyWeight,
    ErrorHint,
    InfoBox,
    Loadable,
    ModalContainer,
    Spinner
} from "@/3514/components"
import { EPath } from "@/3514/constants"
import { type TUserState, useUser } from "@/3514/hooks"
import { EModalId, type TModalDispatcher, useModalDispatcher } from "@/3514/store/modal"
import { isUserCoach } from "@/3514/utils"
import { type IUseTranslation, useTranslation } from "@/hooks"

import { ECoachDashboardQueryKey as EQueryKey } from "./coach.dashboard.api.config"
import AttendancesEntity from "./coach.dashboard.entity.attendances"
import classes from "./coach.dashboard.styles"
import uiConfig from "./coach.dashboard.ui.config"
import {
    ECoachDashboardStaticNotificationType as ENotificationType,
    type TCoachDashboardGetIsStaticNotificationVisibleFunction as TGetIsStaticNotificationVisibleFunction,
    type TCoachDashboardRenderStaticNotificationConfigFunction as TRenderStaticNotificationConfig,
    type TCoachDashboardStaticNotificationConfig as TStaticNotificationConfig,
    type TCoachDashboardStaticNotificationHandlersMap as TStaticNotificationHandlersMap
} from "./coach.dashboard.ui.types"

const { renderStatiNotificationsConfig: renderConfig } = uiConfig

/**
 * @deprecated uses old styles
 */
const SetupScheduleModalContainer: LazyExoticComponent<FC> = lazy(
    (): Promise<{ default: FC }> => import("@/widgets/coach-setup-schedule-modal/ui/CoachSetupScheduleModal")
)

const CoachDashboardNotifications: FC = (): ReactElement => {
    const { t }: IUseTranslation = useTranslation()

    const { user }: TUserState = useUser()

    const qc: QueryClient = useQueryClient()

    const navigate: NavigateFunction = useNavigate()

    const modalDispatcher: TModalDispatcher = useModalDispatcher()

    const staticNotificationsHandlers: RefObject<TStaticNotificationHandlersMap> = useRef({
        [ENotificationType.RelinkCalendar]: (): void => void navigate(EPath.Coach_RelinkAccounts()),
        [ENotificationType.SetUpCalendar]: (): void =>
            modalDispatcher.showModal(EModalId._Legacy_Coach_SetupSchedule, {
                onClose: (): void => modalDispatcher.hideModal(EModalId._Legacy_Coach_SetupSchedule)
            })
    })

    const getIsAttentionBoxVisible: TGetIsStaticNotificationVisibleFunction = useCallback(
        (type: ENotificationType): boolean => {
            switch (type) {
                case ENotificationType.SetUpCalendar:
                    return isUserCoach(user) && !user.isCalendarSetupConfirmed
                case ENotificationType.RelinkCalendar:
                    return isUserCoach(user) && user.isAccountsRelinkingRequired
                default:
                    return false
            }
        },
        [user]
    )

    return (
        <section className={classes.notifications.container()}>
            <ErrorBoundary
                resetKeys={[JSON.stringify(EQueryKey.MarkedAttendances())]}
                fallback={<ErrorHint isVisible containerClassName={classes.notifications.fallback()} />}
            >
                <Loadable
                    component={AttendancesEntity}
                    loadingFallback={<Spinner size={ESpinnerSize.Medium} />}
                    withEnforcedSuspense
                />
            </ErrorBoundary>

            {map<TRenderStaticNotificationConfig, ReactElement>(
                values(renderConfig()),
                (func: TRenderStaticNotificationConfig, index: number): ReactElement => {
                    const notification: TStaticNotificationConfig = func({
                        getIsBoxVisible: getIsAttentionBoxVisible,
                        action: (type: ENotificationType): void => staticNotificationsHandlers.current[type](),
                        qc,
                        t
                    })

                    return notification.isVisible ? (
                        <InfoBox
                            key={`cdnc-${index}`}
                            variant="error"
                            title={notification.title}
                            message={notification.description}
                            action={{
                                children: notification.actionLabel,
                                textWeight: ETypographyWeight.Bold,
                                onClick: notification.action,
                                onHover: notification.hover
                            }}
                        />
                    ) : null
                }
            )}

            <ModalContainer modalId={EModalId._Legacy_Coach_SetupSchedule} container={SetupScheduleModalContainer} />
        </section>
    )
}

CoachDashboardNotifications.displayName = "CoachDashboardNotifications"

export default CoachDashboardNotifications
